import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  useGetAdminQuizSourceMaterialsQuery,
  useGetAdminQuizSourceMaterialDownloadUrlLazyQuery,
  useDeleteAdminQuizSourceMaterialMutation
} from "../../../lib/apollo/graphql/quiz/quiz-generated";
import { useForm } from "../../../hooks/useForm";
import { FETCH_SIZE } from "../../../utilities/constants";
import PageWrapper from "../../common/components/PageWrapper";
import Table from "../../common/components/Table";
import DocumentSearchFilters from "../components/DocumentSearchFilters";
import DocumentTable, { Document } from "../components/DocumentTable";
import DocumentBulkActions from "../components/DocumentBulkActions";
import DocumentUploadModal from "../components/DocumentUploadModal";
import DocumentPagination from "../components/DocumentPagination";

export default function QuizDocumentContainer() {
  const navigate = useNavigate();
  const [filters, onChange] = useForm({
    page: 1,
    limit: FETCH_SIZE,
    searchQuery: "",
  });

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);

  // Fetch source materials from API
  const { data, loading, refetch, error } = useGetAdminQuizSourceMaterialsQuery({
    variables: {
      search: filters.searchQuery ? { search: filters.searchQuery } : undefined,
      pagination: {
        page: filters.page,
        limit: 10,
      },
    },
    fetchPolicy: "cache-and-network",
  });
  const [getDownloadUrl] = useGetAdminQuizSourceMaterialDownloadUrlLazyQuery();
  const [deleteSourceMaterial] = useDeleteAdminQuizSourceMaterialMutation();

  // Transform API data to UI format
  const documents: Document[] = useMemo(() => {
    if (!data?.sourceMaterials?.items) return [];

    return data.sourceMaterials.items.map((material: any): Document => ({
      documentId: material.id,
      title: material.title,
      contentType: material.contentType,
      creator: material.createdBy || "-",
      createdAt: material.createdAt,
      updatedAt: material.updatedAt,
    }));
  }, [data]);

  const totalPages = data?.sourceMaterials?.meta?.totalPages || 1;
  const totalCount = data?.sourceMaterials?.meta?.total || 0;

  const handleSearch = () => {
    // 검색 시 페이지를 1로 리셋
    onChange("page", 1);
    refetch({
      search: filters.searchQuery ? { search: filters.searchQuery } : undefined,
      pagination: {
        page: 1,
        limit: 10,
      },
    });
  };

  const handleSelectAll = (selected: boolean) => {
    if (selected) {
      setSelectedIds(documents.map(doc => doc.documentId));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectDocument = (documentId: string, selected: boolean) => {
    if (selected) {
      setSelectedIds(prev => [...prev, documentId]);
    } else {
      setSelectedIds(prev => prev.filter(selectedId => selectedId !== documentId));
    }
  };

  const handleUpload = () => {
    setIsUploadModalOpen(true);
  };

  const handleUploadModalClose = () => {
    setIsUploadModalOpen(false);
  };

  const handleUploadSuccess = () => {
    refetch();
  };

  const handleDownload = () => {
    // TODO: Implement document download
  };

  const handleDelete = async () => {
    if (selectedIds.length === 0) return;

    const confirmMessage = `선택된 ${selectedIds.length}개의 문서를 삭제하시겠습니까?`;
    if (!window.confirm(confirmMessage)) return;

    try {
      // 선택된 모든 문서 삭제
      await Promise.all(
        selectedIds.map((id) =>
          deleteSourceMaterial({
            variables: { id },
          })
        )
      );
      setSelectedIds([]);
      alert(`${selectedIds.length}개의 문서가 삭제되었습니다.`);
      refetch(); // 삭제 후 목록 새로고침
    } catch (error) {
      console.error("Delete failed:", error);
      alert("문서 삭제에 실패했습니다.");
    }
  };


  const handleDocumentClick = (document: Document) => {
    navigate(`/quiz/documents/${document.documentId}`);
  };

  const handleDocumentDownload = async (documentId: string) => {
    try {
      const { data } = await getDownloadUrl({ variables: { id: documentId } });
      const url = data?.getSourceMaterialDownloadUrl;

      if (!url) {
        alert("파일에 문제가 있습니다. 데이터 관리자에게 문의하세요.");
        return;
      }

      // Open URL in new tab for download
      window.open(url, "_blank");
    } catch (error) {
      console.error("Download URL fetch error:", error);
      alert("파일에 문제가 있습니다. 데이터 관리자에게 문의하세요.");
    }
  };

  const handleDocumentDelete = async (documentId: string) => {
    if (!window.confirm("선택한 문서를 삭제하시겠습니까?")) {
      return;
    }

    try {
      await deleteSourceMaterial({
        variables: { id: documentId },
      });
      alert("문서가 삭제되었습니다.");
      refetch(); // 삭제 후 목록 새로고침
    } catch (error) {
      console.error("Delete error:", error);
      alert("문서 삭제에 실패했습니다.");
    }
  };

  return (
    <PageWrapper>
      <div className="flex flex-col h-full">
        <div className="flex items-center justify-between mb-6 flex-shrink-0">
          <h1 className="text-2xl font-bold">문서관리</h1>
        </div>

        <div className="mb-6 flex-shrink-0">
          <DocumentSearchFilters
            searchQuery={filters.searchQuery}
            onSearchChange={(value) => onChange("searchQuery", value)}
            onSearch={handleSearch}
            onUpload={handleUpload}
            totalCount={totalCount}
          />
        </div>

        <div className="flex-1 min-h-0 mb-4">
          <DocumentTable
            documents={documents}
            selectedIds={selectedIds}
            onSelectAll={handleSelectAll}
            onSelectDocument={handleSelectDocument}
            onDocumentClick={handleDocumentClick}
            onDownload={handleDocumentDownload}
            onDelete={handleDocumentDelete}
            isLoading={loading}
          />
        </div>

        <div className="flex flex-col gap-3 flex-shrink-0" style={{ minHeight: selectedIds.length > 0 ? '80px' : '40px' }}>
          <DocumentPagination
            currentPage={filters.page}
            totalPages={totalPages}
            totalCount={totalCount}
            onPageChange={(page: number) => onChange("page", page)}
          />

          <DocumentBulkActions
            selectedCount={selectedIds.length}
            onDownload={handleDownload}
            onDelete={handleDelete}
          />
        </div>

        <DocumentUploadModal
          isOpen={isUploadModalOpen}
          onClose={handleUploadModalClose}
          onUploadSuccess={handleUploadSuccess}
        />
      </div>
    </PageWrapper>
  );
}